import { IAugmentedJQuery, IComponentOptions, IOnChangesObjectOf, IPromise, IQService, IScope, ITimeoutService, copy, equals } from "angular";
import debounce from "lodash.debounce";
import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { localize } from "@gtmhub/localization";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { handleInsideMouseEvent, handleOutsideMouseEvent } from "@gtmhub/shared/directives/close-on-mousedown";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { IdMap, capitalize, toIdMap } from "@gtmhub/util";
import { GridFilteringBarComponentCtrl } from "../grid-filtering-bar.component";
import { GridFiltersArrayQueryService, MAX_AUTOCOMPLETE_RESULTS } from "./grid-filters-array-query-service";
import { GridFiltersDynamicValueFactory } from "./grid-filters-dynamic-value-factory";
import { GridFiltersTagFactory } from "./grid-filters-tag-factory";
import {
  ArrayFilterType,
  GridBooleanFilterEntry,
  GridFilterDefinitions,
  GridFilterEntry,
  GridFilterOperator,
  GridFilterValue,
  IArrayGridFilter,
  IArrayGridFilterDataLoader,
  IArrayGridFilterDefinition,
  IArrayGridFilterView,
  IDateGridFilter,
  IDateGridFilterDefinition,
  IDateGridFilterView,
  IDateRange,
  IDateRangeViewValue,
  IDynamicValue,
  IEnumGridFilterView,
  IGridFilter,
  IGridFilterDefinition,
  IGridFilterDependency,
  IGridFilterTag,
  IGridFilterView,
  IGridFiltersChange,
  ItemType,
  OrFiltersGroup,
  OrGridFiltersGroupView,
  QueryFn,
  createFilter,
  createFilterDefaultValue,
  dynamicValueEquals,
  entryDisplayName,
  entryValue,
  hasFilterValue,
  hasSelectedFilterValue,
  isMissingOrEmptyOperator,
  isRangeOperator,
  isTeamsPlusMoreDynamicValue,
} from "./models";

type OperatorDisplayName = string | ((definition: IGridFilterDefinition) => string);

export type FilterIndexes = Record<"groupIndex" | "filterIndex", number>;

const createDefaultFilterViewValue = (filter: IGridFilter) => {
  if (filter.dataType === "date" && isRangeOperator(filter.operator)) {
    const isSelectingCustomDateRange = typeof filter.value !== "string";
    return <IDateRangeViewValue>{ isSelectingCustomDateRange, dateRange: copy(filter.value) };
  }

  const valueCopy = copy(filter.value);

  if (filter.dataType === "enum" && typeof filter.value === "string") {
    return { displayName: valueCopy, value: valueCopy };
  }

  return valueCopy;
};

const dependsOnFilterByFieldName = (dependency: IGridFilterDependency, filter: IGridFilter): boolean => dependency.fieldName === filter.fieldName;

const dependsOnFilterByFieldNameAndValue = (dependency: IGridFilterDependency, filter: IGridFilter): boolean =>
  dependsOnFilterByFieldName(dependency, filter) && (!dependency.values?.length || !dependency.values.some((value) => hasSelectedFilterValue(filter, value)));

interface IGridFiltersComponentRequire {
  gridFilteringBar: GridFilteringBarComponentCtrl;
}

export interface IGridFiltersComponentBindings {
  itemTypes?: ItemType[];
  itemType?: ItemType;
  itemTypeChange(args: { itemType: ItemType }): void;
  definitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  filtersChange?(args: { change: IGridFiltersChange }): void;
  isOpen: boolean;
  disabled: boolean;
  closeOnClick: string;
  debounce?: number;
  disableOrGroups?: boolean;
  maxAutoCompleteResults?: number;
  requireUniqueFiltersPerGroup?: boolean;
}

interface IGridFiltersComponentRedux {
  sessions: IdMap<IPlanningSession>;
  assignees: IdMap<IAssignee>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToCtrl = (state: IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState): IGridFiltersComponentRedux => {
  return {
    sessions: toIdMap(state.sessions.items),
    assignees: state.assignees.map,
    reduxItemsFetched: state.assignees.isFetched && state.sessions.isFetched && state.teams.isFetched,
    reduxItemsError: state.assignees.error || state.sessions.error || state.teams.error,
  };
};

/**
 * Provides filtering functionality for grid layouts (e.g., goals, metrics, employees). It must be
 * used within `grid-filtering-bar` as its parent.
 *
 * `item-types` specifies a list of supported items types (goals, metrics, etc.)
 *
 * `item-type` specifies whether the filters are related to goals, metrics, etc.
 *
 * `item-type-change` is fired when the item type is changed.
 *
 * `definitions` contains filter definitions, which contain what one can filter on.
 *
 * `filters` are the applied filters with concrete values.
 *
 * `filters-change` is fired when filters are changed (add, remove, update).
 *
 * `is-open` determines if the filtering popover is open.
 *
 * `close-on-click` specifies in which selectors when clicked to close the popover.
 *
 * `debounce` specifies the amount of time in milliseconds to wait before firing the next filtersChange event.
 *
 * `disable-or-groups` (optional) specifies if we don't want to use "OR" groups in the screen (like in single IB).
 *
 * `require-unique-filters-per-group (optional) if true, only unique filters (by field name) will be allowed in each group
 *
 * @example
 * <grid-filtering-bar>
 *   <grid-filters
 *     item-types="$ctrl.itemTypes"
 *     item-type="'Objective'"
 *     item-type-change="$ctrl.onItemTypeChange(itemType)"
 *     definitions="$ctrl.filterDefinitions"
 *     filters="$ctrl.filters"
 *     filters-change="$ctrl.onFiltersChange()"
 *     is-open="$ctrl.filtersOpen"
 *     debounce="500"
 *     disable-or-groups="false"
 *     require-unique-filters-per-group="false">
 *   </grid-filters>
 * </grid-filtering-bar>
 */
export interface GridFiltersComponentCtrl extends IGridFiltersComponentBindings, IGridFiltersComponentRedux, IGridFiltersComponentRequire {}
export class GridFiltersComponentCtrl extends GtmhubController {
  initializing: boolean;
  arrays: Record<IArrayGridFilterDefinition["arrayType"], IArrayGridFilterDataLoader>;
  filterViews: OrGridFiltersGroupView[];
  isRangeOperator = isRangeOperator;
  isMissingOrEmptyOperator = isMissingOrEmptyOperator;
  filtersWithValueCount: number;

  /**
   * To ensure that same dynamic filters contain the same value within the component, we need
   * to maintain a cache. For example, if we have two filters of type session array and if we
   * add the dynamic filter ActiveSessions, we need to make sure it will contain the same
   * sessions for both filters.
   */
  private dynamicValueCache: Record<string, string[]> = {};

  private operators: Record<GridFilterOperator, OperatorDisplayName>;
  private usedDefinitionsInGroups: Set<string>[] = [];
  private hasNotified = false;
  private debouncedFiltersChange: () => void;
  private areAndOrFiltersEnabled: boolean;

  static $inject = [
    "$element",
    "$q",
    "$scope",
    "$ngRedux",
    "$timeout",
    "PlanningSessionsActions",
    "AssigneeActions",
    "TeamsActions",
    "UIErrorHandlingService",
    "GridFiltersArrayQueryService",
    "GridFiltersTagFactory",
    "GridFiltersDynamicValueFactory",
    "$rootScope",
    "FeatureToggleService",
  ];

  constructor(
    private $element: IAugmentedJQuery,
    private $q: IQService,
    private $scope: IScope & IExtendedRootScopeService,
    private $ngRedux: INgRedux,
    private $timeout: ITimeoutService,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private teamsActions: TeamsActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private gridFiltersArrayQueryService: GridFiltersArrayQueryService,
    private gridFiltersTagFactory: GridFiltersTagFactory,
    private gridFiltersDynamicValueFactory: GridFiltersDynamicValueFactory,
    private $rootScope: ITraceRootScopeService,
    private featureToggleService: FeatureToggleService
  ) {
    super();

    this.initializing = true;
  }

  selectableDefinitions(groupIndex: number): IGridFilterDefinition[] {
    return Object.values(this.definitions).filter((definition) => {
      if (this.usedDefinitionsInGroups[groupIndex].has(definition.fieldName)) {
        return false;
      }

      if (!definition.dependsOn) {
        return true;
      }

      const { dependsOn } = definition;

      // Disallow selection of this filter when it depends on another one that is not used
      // within the same group
      if (!this.usedDefinitionsInGroups[groupIndex].has(dependsOn.fieldName)) {
        return false;
      }

      if (dependsOn.values?.length) {
        const filtersGroup = this.filters[groupIndex];

        // Check if there is any filter within the group that has a value required by the dependency
        const hasAnyFilterWithValue = filtersGroup.some((filter) => dependsOn.values.some((value) => hasSelectedFilterValue(filter, value)));
        if (!hasAnyFilterWithValue) {
          return false;
        }
      }

      return true;
    });
  }

  canCreateMoreFilters(groupIndex: number): boolean {
    return Object.keys(this.definitions || {}).length > this.usedDefinitionsInGroups[groupIndex].size;
  }

  $onInit(): void {
    this.onDestroy(handleOutsideMouseEvent("mousedown", () => this.$timeout(() => (this.isOpen = false)), this.$element, ".gh-daterangepicker", ".gh-datepicker"));

    if (this.closeOnClick) {
      this.onDestroy(handleInsideMouseEvent("click", () => this.$timeout(() => (this.isOpen = false)), ...this.closeOnClick.split(",")));
    }

    this.maxAutoCompleteResults = this.maxAutoCompleteResults || MAX_AUTOCOMPLETE_RESULTS;
    this.arrays = this.createArrays();
    this.operators = this.translateOperators();

    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.$q
      .all([this.$scope.watchUntil(() => this.reduxItemsFetched && !this.reduxItemsError), this.andOrFiltersFeatureToggle()])
      .then(() => this.createFilterViews())
      .then(
        () => {
          this.initializing = false;
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<IGridFiltersComponentBindings>): void {
    if (!this.initializing && (onChangesObj.filters || onChangesObj.definitions)) {
      this.createFilterViews();
    }

    if (onChangesObj.debounce) {
      if (onChangesObj.debounce.currentValue) {
        this.debouncedFiltersChange = debounce(() => {
          this.filtersChange({ change: { initial: false } });
        }, onChangesObj.debounce.currentValue);
      } else {
        this.debouncedFiltersChange = null;
      }
    }
  }

  private andOrFiltersFeatureToggle(): IPromise<boolean> {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.AndOrFilters).then((isEnabled) => (this.areAndOrFiltersEnabled = isEnabled));
  }

  createGroup(): void {
    const lastGroupsIndex = this.filters.length - 1;
    const lastFiltersGroupCopy = copy(this.filters[lastGroupsIndex]);
    const lastFiltersGroupViewCopy = copy(this.filterViews[lastGroupsIndex]);
    this.filters.push(lastFiltersGroupCopy);
    this.usedDefinitionsInGroups.push(new Set(this.usedDefinitionsInGroups[lastGroupsIndex]));
    const newFiltersGroupView = lastFiltersGroupCopy.reduce<OrGridFiltersGroupView>((newGroup, filter, index) => {
      const fieldName = filter.fieldName;
      const definition = this.definitions[fieldName];
      const viewValue = lastFiltersGroupViewCopy[index].viewValue;
      const view: IGridFilterView = { fieldName, definition, filter, viewValue };
      newGroup.push(view);
      return newGroup;
    }, []);
    this.filterViews.push(newFiltersGroupView);

    this.filtersWithValueCount = this.valuedFiltersCount();

    this.trackFiltersGroupAdded({
      groupsCount: this.filterViews.length,
      filtersInGroupCount: newFiltersGroupView.length,
    });
  }

  private trackFiltersGroupAdded({ groupsCount, filtersInGroupCount }: { groupsCount: number; filtersInGroupCount: number }) {
    this.gridFilteringBar.track("Filters Group Added", {
      groups_count: groupsCount,
      filters_in_group_count: filtersInGroupCount,
    });
  }

  removeGroup(groupIndex: number): void {
    const removedFiltersGroup = this.filters.splice(groupIndex, 1)[0];
    this.filterViews.splice(groupIndex, 1);
    this.usedDefinitionsInGroups.splice(groupIndex, 1);

    this.$rootScope.traceAction("filters_group_removed", () => {
      if (removedFiltersGroup.some((filter) => hasFilterValue(filter))) {
        this.notifyFiltersChange();
      }

      this.trackFiltersGroupRemoved({ groupsCount: this.filterViews.length, filtersInGroupCount: removedFiltersGroup.length });
    });
  }

  private trackFiltersGroupRemoved({ groupsCount, filtersInGroupCount }: { groupsCount: number; filtersInGroupCount: number }) {
    this.gridFilteringBar.track("Filters Group Removed", {
      groups_count: groupsCount,
      filters_in_group_count: filtersInGroupCount,
    });
  }

  operatorDisplayName(indexes: FilterIndexes, operator: GridFilterOperator): string {
    const view = this.filterViewAt(indexes);
    if (!(operator in this.operators)) {
      return operator;
    }

    const displayName = this.operators[operator];
    if (typeof displayName === "string") {
      return displayName;
    }

    return displayName(view.definition);
  }

  createFilter(fieldName: string, groupIndex: number): void {
    if (!(fieldName in this.definitions)) {
      throw new Error(`No definition found for field ${fieldName}`);
    }

    const definition = this.definitions[fieldName];
    const filter = createFilter(definition);
    this.filters[groupIndex].push(filter);

    const view: IGridFilterView = {
      fieldName,
      definition,
      filter,
      viewValue: createDefaultFilterViewValue(filter),
    };
    this.filterViews[groupIndex].push(view);
    this.addToUsedDefinitionsInGroup(definition, groupIndex);

    // applies the filter immediately after it has been created
    if (definition.dataType === "boolean" && definition.applyFirstOnCreate) {
      const filterIndex = this.filterViews[groupIndex].length - 1;
      const [firstFilterEntry] = definition.entries;

      this.changeBooleanFilter({ filterIndex, groupIndex }, firstFilterEntry);
    }
  }

  removeFilter(indexes: FilterIndexes): void {
    const { groupIndex, filterIndex } = indexes;
    const filtersGroupView = this.filterViews[groupIndex];
    const filtersGroup = this.filters[groupIndex];

    this.$rootScope.traceAction("remove_filer", () => {
      if (filterIndex < 0 || filterIndex >= filtersGroupView.length) {
        return;
      }

      const view = this.filterViewAt(indexes);
      if (view.definition.readonly) {
        return;
      }

      filtersGroupView.splice(filterIndex, 1);
      filtersGroup.splice(filterIndex, 1);

      const { fieldName, definition, filter } = view;
      this.usedDefinitionsInGroups[groupIndex].delete(fieldName);

      if (hasFilterValue(filter)) {
        this.trackFilterRemoved({
          definition,
          groupsCount: this.filterViews.length,
        });
        this.notifyFiltersChange();
      }

      this.removeDependentFilters(groupIndex, filter, dependsOnFilterByFieldName);
    });
  }

  itemTypeDisplayName(itemType: ItemType): string {
    const itemTypeToLocalizationKey: Record<ItemType, string> = {
      Objective: "objectives",
      "Key Result": "key_results",
      Task: "tasks",
      Team: "teams",
      Employee: "employees",
    };
    return capitalize(localize(itemTypeToLocalizationKey[itemType]));
  }

  changeOperator(indexes: FilterIndexes, operator: GridFilterOperator): void {
    this.$rootScope.traceAction("change_filter_operator", () => {
      const view = this.filterViewAt(indexes);

      const isFromDateRangeOperator = isRangeOperator(view.filter.operator);
      const isToDateRangeOperator = isRangeOperator(operator);

      view.filter.operator = operator;

      if (isFromDateRangeOperator !== isToDateRangeOperator) {
        view.filter.value = createFilterDefaultValue(view.definition, operator);
        view.viewValue = createDefaultFilterViewValue(view.filter);
      }

      if (hasFilterValue(view.filter)) {
        this.notifyFiltersChange();
      }
    });
  }

  changeFilterValue(indexes: FilterIndexes, value: GridFilterValue): void {
    this.$rootScope.traceAction("change_filter_value", () => {
      const view = this.filterViewAt(indexes);

      const hadValue = hasFilterValue(view.filter);
      view.filter.value = value;
      const hasValue = hasFilterValue(view.filter);

      this.notifyFiltersChange();

      if (hadValue && !hasValue) {
        this.trackFilterRemoved({
          definition: view.definition,
          groupsCount: this.filterViews.length,
        });
      } else {
        this.trackFilterAdded({
          definition: view.definition,
          groupsCount: this.filterViews.length,
          isDuplicating: this.isDuplicatingFilterFieldName(indexes, view),
        });
      }
    });
  }

  private isDuplicatingFilterFieldName(indexes: FilterIndexes, view: IGridFilterView) {
    return this.filterViews[indexes.groupIndex].filter((filterView) => filterView.fieldName === view.fieldName).length > 1;
  }

  changeDropdownEnumFilterValue(indexes: FilterIndexes, entry: GridFilterEntry): void {
    const view = this.filterViewAt(indexes) as IEnumGridFilterView | IDateGridFilterView;
    view.viewValue = typeof entry == "string" ? { displayName: entry, value: entry } : entry;
    this.changeFilterValue(indexes, entryValue(entry));
  }

  changeBooleanFilter(indexes: FilterIndexes, entry: GridBooleanFilterEntry): void {
    const view = this.filterViewAt(indexes);
    view.viewValue = entry;

    const operator = entry.operator || "equals";
    if (view.filter.operator !== operator) {
      this.changeOperator(indexes, operator);
    }

    if (view.filter.value !== entry.value) {
      this.changeFilterValue(indexes, entry.value);
    }
  }

  entryDisplayName = entryDisplayName;

  canAddTagToFilter(indexes: FilterIndexes): boolean {
    const view = this.filterViewAt(indexes) as IArrayGridFilterView;
    return typeof view.definition.maxValues === "undefined" || view.viewValue.length < view.definition.maxValues;
  }

  addSessionTagToFilter(indexes: FilterIndexes, sessionId: string): void {
    const sessionTag = this.createSessionTag(sessionId);
    this.addTagToFilter(indexes, sessionTag);
  }

  addTagToFilter(indexes: FilterIndexes, tag: IGridFilterTag): void {
    this.$rootScope.traceAction("add_tag_to_filter", () => {
      const view = this.filterViewAt(indexes) as IArrayGridFilterView;
      if (!view.viewValue) {
        view.viewValue = [];
      }
      if (view.viewValue.findIndex((v) => v.id === tag.id) < 0) {
        view.viewValue.push(tag);
      }

      const trackAndNotifyChange = () => {
        this.trackFilterAdded({
          definition: view.definition,
          groupsCount: this.filterViews.length,
          isDuplicating: this.isDuplicatingFilterFieldName(indexes, view),
        });

        this.notifyFiltersChange();
      };

      if (tag.dynamicValue) {
        this.addDynamicValueToFilter(view.filter, tag, view.definition.arrayType);
      } else {
        if (!view.filter.value) {
          view.filter.value = [];
        }

        if (!view.filter.value.some((filterValue) => filterValue === tag.id)) {
          view.filter.value = [...view.filter.value, tag.id];
        }

        this.trySetEntityDataToFilter(view);
      }

      trackAndNotifyChange();
    });
  }

  removeSessionTagFromFilter(indexes: FilterIndexes, sessionId: string): void {
    const sessionTag = this.createSessionTag(sessionId);
    this.removeTagFromFilter(indexes, sessionTag);
  }

  removeTagFromFilter(indexes: FilterIndexes, tag: IGridFilterTag): void {
    this.$rootScope.traceAction("remove_tag_form_filter", () => {
      const view = this.filterViewAt(indexes) as IArrayGridFilterView;
      if (!view.viewValue) {
        view.viewValue = [];
      }

      const tagIndex = view.viewValue.findIndex((v) => v.id === tag.id);
      if (tagIndex >= 0) {
        view.viewValue.splice(tagIndex, 1);
      }

      if (tag.dynamicValue) {
        view.filter.dynamicValues = view.filter.dynamicValues.filter((dynamicValue) => !dynamicValueEquals(dynamicValue, tag.dynamicValue));
      } else {
        view.filter.value = view.filter.value.filter((id) => id !== tag.id);
        this.trySetEntityDataToFilter(view);
      }

      this.removeDependentFilters(indexes.groupIndex, view.filter, dependsOnFilterByFieldNameAndValue);

      if (!hasFilterValue(view.filter)) {
        this.trackFilterRemoved({
          definition: view.definition,
          groupsCount: this.filterViews.length,
        });
      }

      this.notifyFiltersChange();
    });
  }

  displayArrayGridFilterPlaceholder(view: IArrayGridFilterView): string {
    return view.definition.placeholder || this.arrays[view.definition.arrayType].placeholder;
  }

  selectPredefinedDateRange(indexes: FilterIndexes, entry: GridFilterEntry): void {
    this.$rootScope.traceAction("select_predefined_date_range_for_filter", () => {
      const view = this.filterViewAt(indexes) as IDateGridFilterView;
      view.viewValue = {
        isSelectingCustomDateRange: false,
        dateRange: entryDisplayName(entry),
      };
      view.filter.value = entryValue(entry) as string;
      this.notifyFiltersChange();
    });
  }

  selectCustomDateRange(indexes: FilterIndexes): void {
    this.$rootScope.traceAction("select_custom_date_range_for_filter", () => {
      const view = this.filterViewAt(indexes) as IDateGridFilterView;
      view.viewValue = {
        isSelectingCustomDateRange: true,
        dateRange: "",
      };
      view.filter.value = "";
      this.notifyFiltersChange();
    });
  }

  dateChange(indexes: FilterIndexes): void {
    this.$rootScope.traceAction("date_change_filter", () => {
      const view = this.filterViewAt(indexes) as IDateGridFilterView;
      let formattedDate: string | IDateRange;

      if (isRangeOperator(view.filter.operator)) {
        const viewValue = view.viewValue as IDateRangeViewValue;
        formattedDate = copy(viewValue.dateRange);
      } else {
        if (view.viewValue) {
          formattedDate = dayjs.utc(view.viewValue as string).toISOString();
        }
      }

      view.filter.value = formattedDate;
      this.notifyFiltersChange();
    });
  }

  private notifyFiltersChange(): void {
    if (this.filtersChange) {
      if (this.hasNotified && this.debouncedFiltersChange) {
        this.debouncedFiltersChange();
      } else {
        this.filtersChange({ change: { initial: !this.hasNotified } });
        this.hasNotified = true;
      }
    }

    this.filtersWithValueCount = this.valuedFiltersCount();
  }

  private valuedFiltersCount(): number {
    return this.filters.reduce((totalCount, filtersGroup) => {
      totalCount += filtersGroup.filter((filter) => hasFilterValue(filter)).length;
      return totalCount;
    }, 0);
  }

  private trackFilterAdded({ definition, groupsCount, isDuplicating }: { definition: IGridFilterDefinition; groupsCount: number; isDuplicating: boolean }) {
    this.gridFilteringBar.track("Filter Added", {
      field_name: definition.trackingName || definition.displayName,
      groups_count: groupsCount,
      is_duplicating: isDuplicating,
    });
  }

  private trackFilterRemoved({ definition, groupsCount }: { definition: IGridFilterDefinition; groupsCount: number }) {
    this.gridFilteringBar.track("Filter Removed", {
      field_name: definition.trackingName || definition.displayName,
      groups_count: groupsCount,
    });
  }

  private trySetEntityDataToFilter(view: IArrayGridFilterView) {
    if (view.definition.arrayType === "entity") {
      view.filter.entityData = view.viewValue.map((x) => ({ displayName: x.name, value: x.id }));
    }
  }

  private filterViewAt(indexes: FilterIndexes): IGridFilterView {
    const { groupIndex, filterIndex } = indexes;
    const filtersGroup = this.filterViews[groupIndex];
    if (!filtersGroup) {
      throw new Error(`No such group at index ${groupIndex}`);
    }
    if (filterIndex < 0 || filterIndex >= filtersGroup.length) {
      throw new Error(`No such filter in group ${groupIndex} at index ${filterIndex}`);
    }

    return filtersGroup[filterIndex];
  }

  private addDynamicValueToFilter(filter: IArrayGridFilter, tag: IGridFilterTag, arrayFilterType: ArrayFilterType): void {
    if (!filter.dynamicValues) {
      filter.dynamicValues = [];
    }

    const dynamicValueExists = filter.dynamicValues.some((dynamicValue) => equals(dynamicValue, tag.dynamicValue));
    if (!dynamicValueExists) {
      const dynamicValue = copy(tag.dynamicValue);
      filter.dynamicValues = [...filter.dynamicValues, dynamicValue];
      this.evaluateDynamicValue(dynamicValue, arrayFilterType);
    }
  }

  private evaluateDynamicValue(dynamicValue: IDynamicValue, arrayFilterType: ArrayFilterType): void {
    const cacheKey = isTeamsPlusMoreDynamicValue(dynamicValue) ? `${dynamicValue.type}-${dynamicValue.teamId}` : dynamicValue.type;
    if (cacheKey in this.dynamicValueCache) {
      dynamicValue.evaluated = this.dynamicValueCache[cacheKey];
      return;
    }

    const value = this.gridFiltersDynamicValueFactory.evaluate(dynamicValue, arrayFilterType);
    dynamicValue.evaluated = value;
    this.dynamicValueCache[cacheKey] = value;
  }

  private createFilterViews(): IPromise<unknown> {
    this.usedDefinitionsInGroups = [];
    this.filterViews = [];

    if (!this.filters || !this.definitions) {
      return;
    }

    const viewValuePromises: IPromise<unknown>[] = [];

    this.removeFiltersWithoutDefinition();

    for (let groupIndex = 0; groupIndex < this.filters.length; groupIndex++) {
      const filtersGroup = this.filters[groupIndex];
      this.usedDefinitionsInGroups.push(new Set<string>());
      const groupView: OrGridFiltersGroupView = [];
      this.filterViews.push(groupView);
      for (const filter of filtersGroup) {
        const definition = this.definitions[filter.fieldName];
        const view: IGridFilterView = {
          fieldName: filter.fieldName,
          definition,
          filter,
        };
        groupView.push(view);
        this.addToUsedDefinitionsInGroup(definition, groupIndex);

        const viewValuePromise = this.createFilterViewValue(definition, filter).then((viewValue) => {
          view.viewValue = viewValue;
        });
        viewValuePromises.push(viewValuePromise);

        // load dynamic filter values
        if (filter.dataType === "array") {
          for (const dynamicValue of filter.dynamicValues || []) {
            this.evaluateDynamicValue(dynamicValue, (definition as IArrayGridFilterDefinition).arrayType);
          }
        }
      }
    }

    return this.$q.all(viewValuePromises).then(() => this.notifyFiltersChange());
  }

  private removeFiltersWithoutDefinition(): void {
    for (const filtersGroup of this.filters) {
      let filterIndex = 0;
      while (filterIndex < filtersGroup.length) {
        const filter = filtersGroup[filterIndex];
        if (filter.fieldName in this.definitions) {
          filterIndex += 1;
        } else {
          filtersGroup.splice(filterIndex, 1);
        }
      }
    }
  }

  private createFilterViewValue(definition: IGridFilterDefinition, filter: IGridFilter): IPromise<IGridFilterView["viewValue"]> {
    if (definition.dataType === "array" && filter.dataType === "array") {
      return this.gridFiltersTagFactory.createTags(definition, filter);
    } else if (definition.dataType === "numeric" && filter.dataType === "numeric" && definition.percentage && typeof filter.value === "number") {
      return this.$q.resolve(filter.value * 100);
    } else if (definition.dataType === "date" && filter.dataType === "date") {
      return this.createDateViewValue(definition, filter);
    } else if (definition.dataType === "enum") {
      const entry = definition.entries.find((entry) => entryValue(entry) === filter.value);
      if (entry) {
        return this.$q.resolve(typeof entry === "string" ? { displayName: entry, value: entry } : entry);
      }
    } else if (definition.dataType === "boolean") {
      const entry = definition.entries.find((entry) => (definition.inferValueFromOperator ? entry.operator === filter.operator : entry.value === filter.value));
      return this.$q.resolve(entry);
    }

    return this.$q.resolve(filter.value);
  }

  private createDateViewValue(definition: IDateGridFilterDefinition, filter: IDateGridFilter): IPromise<string | IDateRangeViewValue> {
    const isValidDate = (value: string | IDateRange) => typeof value === "string" && dayjs(value).isValid();

    const isValidDateRange = (value: string | IDateRange) => {
      if (typeof value === "string") {
        if (value) {
          console.warn("Invalid input received for date range. It must be { startDate: '', endDate: '' } or empty string", value);
          return false;
        }
        return true;
      }

      return (!value.startDate && !value.endDate) || (isValidDate(value.startDate) && isValidDate(value.endDate));
    };

    const value = filter.value;
    if (isRangeOperator(filter.operator)) {
      let viewValue: IDateRangeViewValue;
      const predefinedDateRange = (definition.predefinedDateRanges || []).find((dateRange) => dateRange.value === value);
      if (predefinedDateRange) {
        viewValue = { isSelectingCustomDateRange: false, dateRange: predefinedDateRange.displayName };
      } else {
        viewValue = { isSelectingCustomDateRange: true, dateRange: isValidDateRange(value) ? value : "" };
      }
      return this.$q.resolve(viewValue);
    } else {
      return this.$q.resolve(!value || isValidDate(value) ? (value as string) : "");
    }
  }

  private translateOperators(): Record<GridFilterOperator, OperatorDisplayName> {
    return {
      isOneOf: localize("is_one_of"),
      isNotOneOf: localize("is_not_one_of"),
      greater: localize("is_greater_than"),
      less: localize("is_lower_than"),
      isBefore: localize("is_before"),
      isAfter: localize("is_after"),
      isBetween: localize("is_between"),
      isNotBetween: localize("is_not_between"),
      notEqual: (d: IGridFilterDefinition) => localize(d.dataType === "text" ? "is_not" : "is_not_equal_to"),
      equals: (d: IGridFilterDefinition) => localize(d.dataType === "text" ? "is" : "equals_to"),
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
      missingOrEmpty: localize("never").toLowerCase(),
    };
  }

  private createArrays(): Record<IArrayGridFilterDefinition["arrayType"], IArrayGridFilterDataLoader> {
    const actionNames: Record<IArrayGridFilterDefinition["arrayType"], string> = {
      tag: "start_typing_tags",
      assignee: "search_for_users_or_teams",
      user: "search_for_users",
      team: "search_for_teams",
      session: "search_for_session",
      role: "select_roles",
      goal: "search_for_goal",
      okr: "search_for_okr",
      entity: "start_typing",
      static: "start_typing",
    };

    const wrap =
      (action: string, fn: QueryFn): QueryFn =>
      (input: string, definition: IArrayGridFilterDefinition, filter: IArrayGridFilter) => {
        if (typeof definition.maxValues === "number" && definition.maxValues <= (filter.value || []).length) {
          return [];
        }

        return this.$rootScope.traceAction(action, () => fn.call(this.gridFiltersArrayQueryService, input, definition, filter));
      };

    return {
      tag: {
        placeholder: localize(actionNames.tag),
        query: wrap(actionNames.tag, this.gridFiltersArrayQueryService.queryTags),
      },
      assignee: {
        placeholder: localize(actionNames.assignee),
        query: wrap(actionNames.assignee, this.gridFiltersArrayQueryService.queryAssignees),
      },
      user: {
        placeholder: localize(actionNames.user),
        query: wrap(actionNames.user, this.gridFiltersArrayQueryService.queryUsers),
      },
      team: {
        placeholder: localize(actionNames.team),
        query: wrap(actionNames.team, this.gridFiltersArrayQueryService.queryTeams),
      },
      session: {
        placeholder: localize(actionNames.session),
        query: wrap(actionNames.session, this.gridFiltersArrayQueryService.querySessions),
      },
      role: {
        placeholder: localize(actionNames.role),
        query: wrap(actionNames.role, this.gridFiltersArrayQueryService.queryRoles),
      },
      goal: {
        placeholder: localize(actionNames.goal),
        query: wrap(actionNames.goal, this.gridFiltersArrayQueryService.queryGoals),
      },
      okr: {
        placeholder: localize(actionNames.okr),
        query: wrap(actionNames.okr, this.gridFiltersArrayQueryService.queryOkrs),
      },
      entity: {
        placeholder: localize(actionNames.entity),
        query: wrap(actionNames.entity, this.gridFiltersArrayQueryService.queryEntities),
      },
      static: {
        placeholder: localize(actionNames.static),
        query: wrap(actionNames.static, this.gridFiltersArrayQueryService.queryStaticEntries),
      },
    };
  }

  private createSessionTag(sessionId: string): IGridFilterTag {
    if (sessionId === "activeSessions") {
      return this.gridFiltersTagFactory.createActiveSessionsTag();
    } else {
      return this.gridFiltersTagFactory.createSessionTag(sessionId);
    }
  }

  private removeDependentFilters(groupIndex: number, filter: IGridFilter, condition: (dependency: IGridFilterDependency, filter: IGridFilter) => boolean): void {
    // check all filters within the same group to see if there are any
    // dependencies to the updated filter
    const filtersGroupView = this.filterViews[groupIndex];
    for (let i = filtersGroupView.length - 1; i >= 0; i--) {
      const { definition } = filtersGroupView[i];
      if (!definition.dependsOn) {
        continue;
      }

      if (!condition(definition.dependsOn, filter)) {
        continue;
      }

      this.removeFilter({ groupIndex, filterIndex: i });
    }
  }

  private addToUsedDefinitionsInGroup(definition: IGridFilterDefinition, groupIndex: number): void {
    if (!this.areAndOrFiltersEnabled || this.requireUniqueFiltersPerGroup || definition.unique) {
      this.usedDefinitionsInGroups[groupIndex].add(definition.fieldName);
    }
  }
}

export const GridFiltersComponent: IComponentOptions = {
  templateUrl: require("./grid-filters.jade"),
  controller: GridFiltersComponentCtrl,
  require: { gridFilteringBar: "^" },
  bindings: {
    isOpen: "<",
    closeOnClick: "@",
    itemTypes: "<",
    itemType: "<",
    itemTypeChange: "&",
    definitions: "<",
    filters: "<",
    filtersChange: "&",
    disabled: "<ngDisabled",
    debounce: "<",
    disableOrGroups: "<?",
    requireUniqueFiltersPerGroup: "<?",
    maxAutoCompleteResults: "<?",
  },
};
